// ─────────────────────────────────────────────────────────────
// main.ts — Application Entry Point
// ─────────────────────────────────────────────────────────────
import '@/styles/base.css';
import '@/styles/components.css';
import '@/styles/layout.css';
import '@/firebase';

import { initTheme, toggleTheme }               from '@/ui/theme';
import { commitModal, closeModal,
         closeOverlayOutside,
         initModalSwipe }                       from '@/ui/modal';
import { showScreen, switchTab,
         buildNavTabs, buildBottomNav,
         buildMoreDrawerItems,
         toggleMoreDrawer, closeMoreDrawer,
         initHistoryListener,
         restoreTabFromSession }                from '@/ui/navigation';
import { initAuth, doLogin, doLogout,
         changeOwnPassword,
         authErrorMessage }                     from '@/services/auth.service';
import { openChangePasswordModal,
         checkPwdStrength, checkPwdMatch }      from '@/ui/passwordModal';
import { loadTrips, openTripModal,
         deleteTrip, removeCoverPreview,
         previewCover, selectEmoji, selectColor,
         unsubscribeTrips,
         registerGlobals as tripsGlobals }      from '@/screens/trips.screen';
import { loadAdminUsers, filterUsers,
         goUsersPage,
         openUserModal,
         setAdmin, adminChangePassword,
         switchAdminTab, refreshDashboard,
         renderLogsTab, filterLogs,
         registerGlobals as adminGlobals }      from '@/screens/admin.screen';
import { setDashPeriod, filterDashUsers,
         updateDashChart }                      from '@/screens/dashboard.screen';
import { renderAll, openItemModal, delItem,
         openAttachModal, delAttachment,
         registerAllGlobals }                   from '@/tabs/shared';
import { subscribeToTrip,
         unsubscribeFromTrip }                  from '@/services/sync.service';
import { setCurrentTrip, getTrip,
         getTripId, setUser }                  from '@/store/tripState';
import { saveTripField }                        from '@/tabs/viagem';
import { saveBudgetLimit }                      from '@/tabs/orcamento';
import { onBudgetInput }                        from '@/helpers';
import { closeAttachmentViewer,
         isAttachmentViewerRoute,
         openAttachmentViewer }                 from '@/ui/attachmentViewer';
import type { TravelleUser, TabId }             from '@/types';

// ── Show / hide password ──────────────────────────────────────
function togglePassVis(): void {
  const inp = document.getElementById('a-pass') as HTMLInputElement | null;
  const btn = document.querySelector('.pass-toggle') as HTMLButtonElement | null;
  if (!inp) return;
  const show = inp.type === 'password';
  inp.type   = show ? 'text' : 'password';
  if (btn) {
    btn.textContent  = show ? '🙈' : '👁';
    btn.setAttribute('aria-label', show ? 'Ocultar senha' : 'Mostrar senha');
  }
}

// ── Offline sync banner ───────────────────────────────────────
async function initSyncBanner(): Promise<void> {
  try {
    const { ref, onValue } = await import('firebase/database');
    const { db }           = await import('@/firebase');
    const banner  = document.getElementById('offline-banner');
    const dot     = document.getElementById('sync-dot');
    const txt     = document.getElementById('sync-txt');
    onValue(ref(db, '.info/connected'), (snap) => {
      const online = snap.val() === true;
      if (banner) banner.style.display = online ? 'none' : '';
      if (dot)    dot.classList.toggle('off', !online);
      if (txt)    txt.textContent = online ? 'Sincronizado' : 'Offline';
    });
  } catch {
    // Firebase unavailable — silently ignore
  }
}

// ── Register Service Worker ───────────────────────────────────
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').then(reg => {
    // Check for updates on every page load
    reg.update().catch(() => void 0);
  }).catch(err => {
    console.warn('SW registration failed:', err);
  });
}

// ── Build nav HTML ────────────────────────────────────────────
function initNavHTML(): void {
  const navTabsEl  = document.getElementById('nav-tabs');
  const bnavEl     = document.querySelector('.bnav-scroll');
  const moreGridEl = document.getElementById('bnav-more-grid');
  if (navTabsEl)  navTabsEl.innerHTML  = buildNavTabs();
  if (bnavEl)     bnavEl.innerHTML     = buildBottomNav();
  if (moreGridEl) moreGridEl.innerHTML = buildMoreDrawerItems();
}

// ── Update nav bar title + color ──────────────────────────────
function updateNavTitle(): void {
  const trip   = getTrip();
  const navEl  = document.getElementById('nav-trip-title');
  const appNav = document.querySelector('.app-nav') as HTMLElement | null;
  if (navEl)  navEl.textContent = `${trip.emoji ?? '✈️'} ${trip.name ?? ''}`;
  if (appNav) {
    if (trip.color) appNav.style.setProperty('--trip-color', trip.color);
    appNav.classList.toggle('colored', !!trip.color);
  }
}

// ── Open trip ─────────────────────────────────────────────────
async function openTrip(tripId: string): Promise<void> {
  try {
    setCurrentTrip(tripId);
    showScreen('app');
    subscribeToTrip(tripId, () => { renderAll(); updateNavTitle(); });
    renderAll();
    updateNavTitle();
    const savedTab = restoreTabFromSession();
    switchTab(savedTab);
  } catch (e) {
    const { toastError } = await import('@/ui/toast');
    toastError('Erro ao abrir viagem: ' + (e instanceof Error ? e.message : String(e)));
  }
}

// ── Restore session on page refresh ──────────────────────────
async function restoreSession(): Promise<void> {
  const tripId = getTripId(); // restored from sessionStorage in store
  if (!tripId) return;

  // If there's a saved trip, re-open it after auth
  // (openTrip is called from onLogin if tripId exists)
}

// ── Wire ALL globals ──────────────────────────────────────────
function wireGlobals(): void {
  const w = window as unknown as Record<string, unknown>;

  w['__handleLogin']           = handleLogin;
  w['__togglePassVis']         = togglePassVis;
  w['__showScreen']            = (name: string) => {
    showScreen(name as Parameters<typeof showScreen>[0]);
    if (name === 'trips') { unsubscribeTrips(); unsubscribeFromTrip(); setCurrentTrip(null); loadTrips(); }
    if (name === 'admin') {
      loadAdminUsers();
      switchAdminTab('dashboard');
    }
  };
  w['__switchTab']             = (id: TabId) => switchTab(id);
  w['__toggleMoreDrawer']      = toggleMoreDrawer;
  w['__closeMoreDrawer']       = closeMoreDrawer;
  w['__toggleTheme']           = toggleTheme;
  w['__closeAttachmentViewer'] = closeAttachmentViewer;
  w['__commitModal']           = commitModal;
  w['__commitModalClose']      = closeModal;
  w['__closeModal']            = closeOverlayOutside;
  w['__doLogout']              = async () => {
    // Mark user as unauthenticated FIRST so any in-flight Firebase callbacks
    // that fire during logout are silently ignored
    setUser(null);
    unsubscribeTrips();
    unsubscribeFromTrip();
    setCurrentTrip(null);
    await doLogout();
    showScreen('auth');
  };

  // Trips
  w['__openTrip']              = openTrip;
  w['__openTripModal']         = openTripModal;
  w['__deleteTrip']            = deleteTrip;
  w['__removeCoverPreview']    = removeCoverPreview;
  w['__previewCover']          = previewCover;
  w['__selectEmoji']           = selectEmoji;
  w['__selectColor']           = selectColor;

  // Admin
  w['__loadAdminUsers']        = loadAdminUsers;
  w['__filterUsers']           = filterUsers;
  w['__goUsersPage']           = goUsersPage;
  w['__openUserModal']         = openUserModal;
  w['__setAdmin']              = setAdmin;
  w['__adminChangePassword']   = adminChangePassword;
  w['__switchAdminTab']        = switchAdminTab;
  w['__removeCurrency']        = async (code: string) => {
    const { getTrip } = await import('@/store/tripState');
    const { saveTrip } = await import('@/services/sync.service');
    const { renderAll } = await import('@/tabs/shared');
    const currencies = (getTrip().currencies ?? ['BRL']).filter((c: string) => c !== code);
    await saveTrip('currencies', currencies);
    renderAll();
  };
  w['__refreshDashboard']      = refreshDashboard;
  w['__setDashPeriod']         = setDashPeriod;
  w['__filterDashUsers']       = filterDashUsers;
  w['__updateDashChart']       = updateDashChart;
  w['__renderLogsTab']         = renderLogsTab;
  w['__filterLogs']            = filterLogs;
  w['__clearLogFilters']       = clearLogFilters;

  // Own password
  w['__openChangeMyPassword']  = () => {
    const email = (w['__currentUserEmail'] as string) ?? '';
    openChangePasswordModal(email, async (newPass) => {
      const curPass = (document.getElementById('f-curpass') as HTMLInputElement)?.value ?? '';
      await changeOwnPassword(curPass, newPass);
    }, true);
  };

  // Password modal
  w['__checkPwdStrength']      = checkPwdStrength;
  w['__checkPwdMatch']         = checkPwdMatch;

  // Item CRUD
  w['__openModal']             = openItemModal;
  w['__delItem']               = delItem;
  w['__openAttachModal']       = openAttachModal;
  w['__delAttachment']         = delAttachment;

  // Field savers
  w['__saveTripField']         = saveTripField;
  w['__saveBudgetLimit']       = saveBudgetLimit;
  w['__onBudgetInput']         = onBudgetInput;

  // Register module globals
  tripsGlobals();
  adminGlobals();
  registerAllGlobals();

  // openTrip override AFTER tripsGlobals() so our enhanced version wins
  w['__openTrip']              = openTrip;
}

// ── Clear log filters helper
function clearLogFilters(): void {
  const fields = ['log-search', 'log-date', 'log-level', 'log-action'];
  fields.forEach(id => {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (el) el.value = '';
  });
  filterLogs();
}

// ── Login handler ─────────────────────────────────────────────
async function handleLogin(): Promise<void> {
  const email  = (document.getElementById('a-email') as HTMLInputElement)?.value.trim() ?? '';
  const pass   = (document.getElementById('a-pass')  as HTMLInputElement)?.value ?? '';
  const errEl  = document.getElementById('auth-err') as HTMLElement;
  const btn    = document.getElementById('btn-login') as HTMLButtonElement;

  errEl.style.display = 'none';
  if (!email || !pass) {
    errEl.textContent   = 'Preencha e-mail e senha.';
    errEl.style.display = 'block';
    return;
  }
  btn.disabled = true; btn.textContent = 'Entrando...';
  try {
    await doLogin(email, pass);
  } catch (e) {
    errEl.textContent   = authErrorMessage((e as { code?: string }).code ?? '');
    errEl.style.display = 'block';
  } finally {
    btn.disabled = false; btn.textContent = 'Entrar';
  }
}

// ── Auth callbacks ────────────────────────────────────────────
async function onLogin(user: TravelleUser, isAdmin: boolean): Promise<void> {
  (window as unknown as Record<string, unknown>)['__currentUserEmail'] = user.email;
  initSyncBanner();

  const loggedAs = document.getElementById('logged-as');
  if (loggedAs) loggedAs.textContent = user.displayName ?? user.email;

  const adminBtn = document.getElementById('btn-admin') as HTMLElement | null;
  if (adminBtn) adminBtn.style.display = isAdmin ? '' : 'none';

  if (isAttachmentViewerRoute()) {
    showScreen('attachment');
    await openAttachmentViewer();
    return;
  }

  // Restore last open trip if session has one — verify access first
  const savedTripId = getTripId();
  if (savedTripId) {
    try {
      const { get, ref } = await import('firebase/database');
      const { db }       = await import('@/firebase');
      const snap = await get(ref(db, `userTrips/${user.uid}/${savedTripId}`));
      if (snap.exists()) {
        await openTrip(savedTripId);
      } else {
        // Trip no longer accessible — clear session and go to trips list
        setCurrentTrip(null);
        showScreen('trips');
        loadTrips();
        switchTab('overview');
      }
    } catch {
      setCurrentTrip(null);
      showScreen('trips');
      loadTrips();
      switchTab('overview');
    }
  } else {
    showScreen('trips');
    loadTrips();
    switchTab('overview');
  }
}

function onLogout(): void {
  unsubscribeTrips();
  unsubscribeFromTrip();
  setCurrentTrip(null);
  showScreen('auth');
}

// ── Bootstrap ─────────────────────────────────────────────────
function bootstrap(): void {
  registerServiceWorker();
  initTheme();
  initNavHTML();
  wireGlobals();
  initHistoryListener();
  initModalSwipe();

  // Login form
  document.getElementById('btn-login')
    ?.addEventListener('click', handleLogin);
  document.getElementById('a-pass')
    ?.addEventListener('keydown', (e) => { if (e.key === 'Enter') handleLogin(); });

  // Modal overlay click-outside
  document.getElementById('overlay')
    ?.addEventListener('click', closeOverlayOutside as unknown as EventListener);

  // Auth state — Firebase will trigger onLogin/onLogout
  initAuth(onLogin, onLogout);
}

bootstrap();
